import { ref } from 'vue'
import authService from '@/services/authService'

/**
 * Composable that provides the authenticated user.
 *
 * The router guard already calls `authService.getCurrentUser()` on every
 * navigation and caches the result in localStorage, so `getCachedUser()`
 * is almost always sufficient.  We fall back to a network call only when
 * the cache is empty (edge-case: deep-link before guard finished).
 *
 * Usage:
 *   const { user, loading, loadUser } = useAuth()
 *   onMounted(() => loadUser())
 *
 * After `loadUser()` resolves:
 *   - `user.value`   → full user object ({ id, name, email, is_admin, … }) or null
 *   - `loading.value` → false
 */
export function useAuth() {
  const user = ref(null)
  const loading = ref(true)

  async function loadUser() {
    loading.value = true
    try {
      // 1. Fast path — router guard already verified & cached the user
      let cached = authService.getCachedUser()
      if (cached && cached.id) {
        user.value = cached
        loading.value = false
        return cached
      }

      // 2. Fallback — hit the backend (shouldn't normally happen)
      const fresh = await authService.getCurrentUser()
      if (fresh && fresh.id) {
        user.value = fresh
      } else {
        user.value = null
      }
      return user.value
    } catch (e) {
      console.error('[useAuth] Erro ao carregar usuário:', e)
      user.value = null
      return null
    } finally {
      loading.value = false
    }
  }

  return { user, loading, loadUser }
}
